import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useMemo, useCallback } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useSelectTextQuiz } from "../hooks/use-select-text-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import { AnimatePresence } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/components/AnimatePresence/index.js";
const StudentSelectBlank = memo(function StudentSelectBlank2({
  quizId,
  index,
  content,
  selectedOptionIdx,
  isOpen,
  isSolved,
  isViewSolved,
  isCorrectAnswer,
  shouldShowCorrectAnswers,
  correctAnswerIndex,
  onToggle
}) {
  const { t } = useTranslation("quiz");
  const options = useMemo(() => {
    return content.split("|").map((s) => s.trim());
  }, [content]);
  const selectedOption = selectedOptionIdx !== null ? options[selectedOptionIdx] : null;
  const showAsCorrect = isSolved && isCorrectAnswer;
  const showAsWrong = isSolved && selectedOptionIdx !== null && !isCorrectAnswer && !shouldShowCorrectAnswers;
  const showCorrectOverride = shouldShowCorrectAnswers && isSolved && !isCorrectAnswer;
  const correctAnswer = options[correctAnswerIndex ?? 0] ?? options[0];
  const isCompleted = isSolved || isViewSolved;
  const hasValue = selectedOption !== null;
  return /* @__PURE__ */ jsx("span", { className: "inline-block my-1", children: /* @__PURE__ */ jsx(
    "button",
    {
      onClick: (e) => {
        e.stopPropagation();
        if (!isCompleted) onToggle(index);
      },
      disabled: isCompleted,
      className: cn(
        "rounded-2xl mx-2 font-medium inline-flex items-center justify-center gap-2 px-4 h-12 text-[24px]",
        // 완료 상태 (정답/오답/비활성)
        showCorrectOverride && "bg-[#D8F6EF] text-[#3BD0AE] border-none [box-shadow:unset] pointer-events-none",
        showAsCorrect && !showCorrectOverride && "bg-[#D8F6EF] text-[#3BD0AE] border-none [box-shadow:unset] pointer-events-none",
        showAsWrong && "bg-[#FFE1DF] text-[#FF675E] border-none [box-shadow:unset] pointer-events-none",
        isViewSolved && !isSolved && "border-none [box-shadow:unset] pointer-events-none",
        // 미완료 상태
        !isCompleted && !isOpen && !hasValue && "border-2 border-[#BBBBBB] [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-[#55C3FF] hover:border-[#55C3FF] hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]",
        !isCompleted && isOpen && "border-2 border-[#55C3FF] bg-[#DDF3FF] min-w-[96px]",
        !isCompleted && !isOpen && hasValue && "border-2 border-[#55C3FF] bg-[#DDF3FF]"
      ),
      children: /* @__PURE__ */ jsx("span", { className: cn(isOpen && !hasValue && "invisible"), children: showCorrectOverride ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswer }) : hasValue ? /* @__PURE__ */ jsx(KatexText, { text: selectedOption }) : t("types.selectText.placeholder") })
    }
  ) }, `select-${quizId}-${index}`);
});
function SelectTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    openDropdown,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit
  } = useSelectTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const allBlankOptions = useMemo(() => {
    const matches = quiz.problem.match(/\{\{([^}]+)\}\}/g) || [];
    return matches.map((m) => m.slice(2, -2).split("|").map((s) => s.trim()));
  }, [quiz.problem]);
  const currentOptions = openDropdown !== null ? allBlankOptions[openDropdown] ?? [] : [];
  const currentSelectedIdx = openDropdown !== null && answers[openDropdown] !== "" ? parseInt(answers[openDropdown]) : null;
  const handlePanelSelect = useCallback((optIdx) => {
    if (openDropdown !== null) {
      handleSelect(openDropdown, optIdx);
    }
  }, [openDropdown, handleSelect]);
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && selectCount > 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${problemKey}`,
      (index, content) => {
        var _a, _b;
        const selectedOptionIdx = answers[index] !== "" ? parseInt(answers[index]) : null;
        const isOpen = openDropdown === index;
        const isCorrectByCorrects = (_a = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a[index];
        const isCorrectAnswer = isCorrectByCorrects !== void 0 && isCorrectByCorrects !== null ? isCorrectByCorrects : selectedOptionIdx === 0;
        const correctAnswerFromServer = (_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index];
        const correctAnswerIndex = typeof correctAnswerFromServer === "number" ? correctAnswerFromServer : typeof correctAnswerFromServer === "string" ? parseInt(correctAnswerFromServer) : null;
        return /* @__PURE__ */ jsx(
          StudentSelectBlank,
          {
            quizId,
            index,
            content,
            selectedOptionIdx,
            isOpen,
            isSolved,
            isViewSolved,
            isCorrectAnswer,
            shouldShowCorrectAnswers,
            correctAnswerIndex,
            onToggle: handleToggleDropdown
          },
          `select-${quizId}-${problemKey}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, openDropdown, isSolved, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, selectCount, quiz.additionalImages, solveResult == null ? void 0 : solveResult.corrects, solveResult == null ? void 0 : solveResult.correctAnswers, problemKey, handleToggleDropdown]);
  const isCompleted = isSolved || isViewSolved;
  const panelOpen = openDropdown !== null && !isCompleted;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.selectText.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "text-2xl font-medium text-gray-700 leading-[48px]", children: renderedProblem }),
    /* @__PURE__ */ jsx(AnimatePresence, { children: panelOpen && currentOptions.length > 0 && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 20 },
        transition: { type: "tween", duration: 0.2, ease: "easeOut" },
        className: "w-full flex items-center justify-center gap-6 px-4",
        children: currentOptions.map((option, optIdx) => {
          const isFix = option.startsWith("fix:");
          if (isFix) return null;
          const isSelected = currentSelectedIdx === optIdx;
          return /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handlePanelSelect(optIdx),
              className: cn(
                "h-[92px] text-[20px] text-center overflow-hidden font-medium flex flex-wrap bg-white justify-center items-center border-2 rounded-3xl grow cursor-pointer select-none py-4 px-6",
                isSelected ? "text-[#55C3FF] border-[#55C3FF] [box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]" : "text-gray-900 border-[#BBBBBB] [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-[#55C3FF] hover:border-[#55C3FF] hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]"
              ),
              children: /* @__PURE__ */ jsx(KatexText, { text: option })
            },
            optIdx
          );
        })
      }
    ) }),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  SelectTextStudent
};
//# sourceMappingURL=select-text-student.js.map
